import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, fromEvent, timer } from "rxjs";
import { map, filter, debounceTime } from "rxjs/operators";

export interface PerformanceMetric {
  name: string;
  value: number;
  unit: string;
  timestamp: number;
  category: "navigation" | "resource" | "user" | "custom";
}

export interface PerformanceReport {
  timestamp: number;
  url: string;
  userAgent: string;
  metrics: PerformanceMetric[];
  summary: {
    totalLoadTime: number;
    firstContentfulPaint: number;
    largestContentfulPaint: number;
    firstInputDelay: number;
    cumulativeLayoutShift: number;
    timeToInteractive: number;
  };
}

export interface PerformanceConfig {
  enabled: boolean;
  sampleRate: number;
  maxMetrics: number;
  reportInterval: number;
  autoReport: boolean;
  endpoint?: string;
}

@Injectable({
  providedIn: "root",
})
export class PerformanceService {
  private metricsSubject = new BehaviorSubject<PerformanceMetric[]>([]);
  private config: PerformanceConfig = {
    enabled: true,
    sampleRate: 1.0,
    maxMetrics: 1000,
    reportInterval: 30000, // 30 segundos
    autoReport: true,
  };

  private performanceObserver?: PerformanceObserver;
  private reportTimer?: any;

  constructor() {
    this.initializePerformanceMonitoring();
  }

  /**
   * Inicializar monitoramento de performance
   */
  private initializePerformanceMonitoring(): void {
    if (!this.config.enabled || !this.isPerformanceSupported()) {
      return;
    }

    this.setupPerformanceObserver();
    this.setupNavigationTiming();
    this.setupResourceTiming();
    this.setupUserTiming();
    this.setupCoreWebVitals();

    if (this.config.autoReport) {
      this.startAutoReporting();
    }
  }

  /**
   * Verificar se Performance API é suportada
   */
  private isPerformanceSupported(): boolean {
    return "performance" in window && "PerformanceObserver" in window;
  }

  /**
   * Configurar Performance Observer
   */
  private setupPerformanceObserver(): void {
    if (!("PerformanceObserver" in window)) {
      return;
    }

    try {
      this.performanceObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry) => this.processPerformanceEntry(entry));
      });

      // Observar diferentes tipos de métricas
      this.performanceObserver.observe({ entryTypes: ["navigation", "resource", "measure", "paint"] });
    } catch (error) {}
  }

  /**
   * Configurar Navigation Timing
   */
  private setupNavigationTiming(): void {
    if (!("performance" in window) || !performance.getEntriesByType) {
      return;
    }

    // Aguardar carregamento completo
    window.addEventListener("load", () => {
      setTimeout(() => {
        const navigationEntries = performance.getEntriesByType("navigation") as PerformanceNavigationTiming[];

        if (navigationEntries.length > 0) {
          const nav = navigationEntries[0];
          this.addMetric("navigation", "totalLoadTime", nav.loadEventEnd - nav.fetchStart, "ms");
          this.addMetric("navigation", "domContentLoaded", nav.domContentLoadedEventEnd - nav.fetchStart, "ms");
          this.addMetric("navigation", "firstByte", nav.responseStart - nav.fetchStart, "ms");
          this.addMetric("navigation", "dnsLookup", nav.domainLookupEnd - nav.domainLookupStart, "ms");
          this.addMetric("navigation", "tcpConnection", nav.connectEnd - nav.connectStart, "ms");
          this.addMetric(
            "navigation",
            "sslHandshake",
            nav.secureConnectionStart ? nav.connectEnd - nav.secureConnectionStart : 0,
            "ms",
          );
        }
      }, 0);
    });
  }

  /**
   * Configurar Resource Timing
   */
  private setupResourceTiming(): void {
    if (!("performance" in window) || !performance.getEntriesByType) {
      return;
    }

    // Monitorar recursos carregados
    const resourceEntries = performance.getEntriesByType("resource") as PerformanceResourceTiming[];

    resourceEntries.forEach((entry) => {
      const duration = entry.responseEnd - entry.startTime;
      const size = entry.transferSize || 0;

      this.addMetric("resource", `${entry.name}_duration`, duration, "ms");
      this.addMetric("resource", `${entry.name}_size`, size, "bytes");
    });
  }

  /**
   * Configurar User Timing
   */
  private setupUserTiming(): void {
    if (!("performance" in window) || !performance.getEntriesByType) {
      return;
    }

    // Monitorar medidas customizadas
    const measureEntries = performance.getEntriesByType("measure") as PerformanceMeasure[];

    measureEntries.forEach((entry) => {
      this.addMetric("user", entry.name, entry.duration, "ms");
    });
  }

  /**
   * Configurar Core Web Vitals
   */
  private setupCoreWebVitals(): void {
    // First Contentful Paint (FCP)
    this.observePaint("first-contentful-paint", "firstContentfulPaint");

    // Largest Contentful Paint (LCP)
    this.observeLargestContentfulPaint();

    // First Input Delay (FID)
    this.observeFirstInputDelay();

    // Cumulative Layout Shift (CLS)
    this.observeCumulativeLayoutShift();
  }

  /**
   * Observar First Contentful Paint
   */
  private observePaint(entryName: string, metricName: string): void {
    if (!("PerformanceObserver" in window)) {
      return;
    }

    try {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry) => {
          this.addMetric("navigation", metricName, entry.startTime, "ms");
        });
      });

      observer.observe({ entryTypes: ["paint"] });
    } catch (error) {}
  }

  /**
   * Observar Largest Contentful Paint
   */
  private observeLargestContentfulPaint(): void {
    if (!("PerformanceObserver" in window)) {
      return;
    }

    try {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1];
        this.addMetric("navigation", "largestContentfulPaint", lastEntry.startTime, "ms");
      });

      observer.observe({ entryTypes: ["largest-contentful-paint"] });
    } catch (error) {}
  }

  /**
   * Observar First Input Delay
   */
  private observeFirstInputDelay(): void {
    if (!("PerformanceObserver" in window)) {
      return;
    }

    try {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry) => {
          const fid = (entry as any).processingStart - entry.startTime;
          this.addMetric("navigation", "firstInputDelay", fid, "ms");
        });
      });

      observer.observe({ entryTypes: ["first-input"] });
    } catch (error) {}
  }

  /**
   * Observar Cumulative Layout Shift
   */
  private observeCumulativeLayoutShift(): void {
    if (!("PerformanceObserver" in window)) {
      return;
    }

    try {
      let clsValue = 0;
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry) => {
          if (!(entry as any).hadRecentInput) {
            clsValue += (entry as any).value;
          }
        });
        this.addMetric("navigation", "cumulativeLayoutShift", clsValue, "score");
      });

      observer.observe({ entryTypes: ["layout-shift"] });
    } catch (error) {}
  }

  /**
   * Processar entrada de performance
   */
  private processPerformanceEntry(entry: PerformanceEntry): void {
    const category = this.getEntryCategory(entry);
    const value = this.getEntryValue(entry);
    const unit = this.getEntryUnit(entry);

    this.addMetric(category, entry.name, value, unit);
  }

  /**
   * Obter categoria da entrada
   */
  private getEntryCategory(entry: PerformanceEntry): "navigation" | "resource" | "user" | "custom" {
    if (entry.entryType === "navigation") return "navigation";
    if (entry.entryType === "resource") return "resource";
    if (entry.entryType === "measure") return "user";
    return "custom";
  }

  /**
   * Obter valor da entrada
   */
  private getEntryValue(entry: PerformanceEntry): number {
    if (entry.entryType === "navigation") {
      return (entry as PerformanceNavigationTiming).loadEventEnd - (entry as PerformanceNavigationTiming).fetchStart;
    }
    if (entry.entryType === "resource") {
      return (entry as PerformanceResourceTiming).responseEnd - (entry as PerformanceResourceTiming).startTime;
    }
    return entry.duration;
  }

  /**
   * Obter unidade da entrada
   */
  private getEntryUnit(entry: PerformanceEntry): string {
    if (entry.entryType === "navigation" || entry.entryType === "resource" || entry.entryType === "measure") {
      return "ms";
    }
    return "score";
  }

  /**
   * Adicionar métrica
   */
  addMetric(category: "navigation" | "resource" | "user" | "custom", name: string, value: number, unit: string): void {
    if (!this.config.enabled) {
      return;
    }

    const metric: PerformanceMetric = {
      name,
      value,
      unit,
      timestamp: Date.now(),
      category,
    };

    const currentMetrics = this.metricsSubject.value;
    const newMetrics = [...currentMetrics, metric];

    // Limitar número de métricas
    if (newMetrics.length > this.config.maxMetrics) {
      newMetrics.splice(0, newMetrics.length - this.config.maxMetrics);
    }

    this.metricsSubject.next(newMetrics);
  }

  /**
   * Obter métricas atuais
   */
  getMetrics(): PerformanceMetric[] {
    return this.metricsSubject.value;
  }

  /**
   * Obter observable das métricas
   */
  getMetrics$(): Observable<PerformanceMetric[]> {
    return this.metricsSubject.asObservable();
  }

  /**
   * Obter métricas por categoria
   */
  getMetricsByCategory(category: string): PerformanceMetric[] {
    return this.metricsSubject.value.filter((metric) => metric.category === category);
  }

  /**
   * Obter métricas por nome
   */
  getMetricsByName(name: string): PerformanceMetric[] {
    return this.metricsSubject.value.filter((metric) => metric.name === name);
  }

  /**
   * Obter última métrica por nome
   */
  getLastMetricByName(name: string): PerformanceMetric | undefined {
    const metrics = this.getMetricsByName(name);
    return metrics.length > 0 ? metrics[metrics.length - 1] : undefined;
  }

  /**
   * Gerar relatório de performance
   */
  generateReport(): PerformanceReport {
    const metrics = this.getMetrics();
    const navigationMetrics = this.getMetricsByCategory("navigation");

    const summary = {
      totalLoadTime: this.getLastMetricByName("totalLoadTime")?.value || 0,
      firstContentfulPaint: this.getLastMetricByName("firstContentfulPaint")?.value || 0,
      largestContentfulPaint: this.getLastMetricByName("largestContentfulPaint")?.value || 0,
      firstInputDelay: this.getLastMetricByName("firstInputDelay")?.value || 0,
      cumulativeLayoutShift: this.getLastMetricByName("cumulativeLayoutShift")?.value || 0,
      timeToInteractive: this.getLastMetricByName("timeToInteractive")?.value || 0,
    };

    return {
      timestamp: Date.now(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      metrics,
      summary,
    };
  }

  /**
   * Iniciar relatório automático
   */
  private startAutoReporting(): void {
    this.reportTimer = setInterval(() => {
      const report = this.generateReport();
      this.sendReport(report);
    }, this.config.reportInterval);
  }

  /**
   * Enviar relatório
   */
  private async sendReport(report: PerformanceReport): Promise<void> {
    if (!this.config.endpoint) {
      return;
    }

    try {
      await fetch(this.config.endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(report),
      });
    } catch (error) {}
  }

  /**
   * Configurar serviço
   */
  configure(config: Partial<PerformanceConfig>): void {
    this.config = { ...this.config, ...config };

    if (config.enabled !== undefined) {
      if (config.enabled) {
        this.initializePerformanceMonitoring();
      } else {
        this.stopMonitoring();
      }
    }
  }

  /**
   * Parar monitoramento
   */
  stopMonitoring(): void {
    if (this.performanceObserver) {
      this.performanceObserver.disconnect();
    }

    if (this.reportTimer) {
      clearInterval(this.reportTimer);
    }
  }

  /**
   * Limpar métricas
   */
  clearMetrics(): void {
    this.metricsSubject.next([]);
  }

  /**
   * Obter estatísticas
   */
  getStats(): any {
    const metrics = this.getMetrics();
    const categories = [...new Set(metrics.map((m) => m.category))];

    return {
      totalMetrics: metrics.length,
      categories: categories.length,
      timeRange:
        metrics.length > 0
          ? {
              start: Math.min(...metrics.map((m) => m.timestamp)),
              end: Math.max(...metrics.map((m) => m.timestamp)),
            }
          : null,
      averageValues: categories.reduce((acc, category) => {
        const categoryMetrics = metrics.filter((m) => m.category === category);
        acc[category] =
          categoryMetrics.length > 0
            ? categoryMetrics.reduce((sum, m) => sum + m.value, 0) / categoryMetrics.length
            : 0;
        return acc;
      }, {}),
    };
  }
}
